import React, { useEffect, useState, useMemo } from "react";
import { MassUpdateProps } from "../action-types";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Autocomplete from "@mui/material/Autocomplete";
import { TextField } from "@mui/material";
import { leadEditableFields } from "../../lead-filter/lead-filter.constant";
import { massUpdateLeads } from "../../../../api/lead";
import { AlertComponent } from "../../../../common/components";
import { useSetLoading } from "../../../../common/hooks/appearance";
import { SelectInputComponent } from "../../../../common/components";
import dynamicFieldValidationSchema from "../input-validation";
import {
  LEAD_STATUS_OPTIONS,
  INTERESTED_PRODUCT_OPTIONS,
  RATING_OPTIONS,
} from "../../../../constants/leadDropdown";
import "../actions.scss";
import "../../../leads/lead-view/lead-view-details/lead-view-details-edit/lead-view-edit-view-full-info/lead-view-edit-view-full-info.scss";
import { makeStyles } from "@mui/styles";
import CountryCodeWithPhoneNumber from "../../../../common/components/phone-number-input-component/country-code-phone-numner-input";
import { isOnlyCountryCode } from "../../../../utils/utils";
import { useLeadOwnerOptions } from "../../../../utils/getLeadOwnerOptions";
import { useLogin } from "../../../../common/hooks/useUser";
import { useResourcePermissions } from "../../../../common/hooks/resource-permissions";
import MODULES from "../../../../constants/modules";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

const MassUpdate: React.FC<MassUpdateProps> = ({
  leadsIds,
  isOpen,
  setMenuItems,
  selectedActions,
  selectedLeadActionScope,
  setselectedLeadActionScope,
  fetchLeads,
  totalRecords,
  handleMassActionMenuClose,
  setSelectedLeadsMap,
  leadsData = [],
}) => {
  const LEAD_OWNER_OPTIONS = useLeadOwnerOptions();
  const [selectedField, setSelectedField] = useState<{
    label: string;
    id: number;
    value: string;
  }>({
    label: "",
    id: 0,
    value: "",
  });
  const [inputValue, setInputValue] = useState<string>("");
  const [disableUpdate, setDisableUpdate] = useState(true);
  const [error, setError] = useState("");
  const [isLoading, setLoading] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // true if screen ≤ 600px
  const [selectedInterestedProduct, setSelectedInterestedProduct] = useState(
    []
  );
  const dropdownLabels = [
    "Interested Product",
    "Confidence Score",
    "Lead Status",
    "Lead Owner",
    "Fax Number",
  ];
  const { tenantDetails, userDetails } = useLogin();
  const { canWrite } = useResourcePermissions(MODULES.ASSIGN_LEAD_OWNER);

  const validateFieldInput = async () => {
    const fieldKey = selectedField.value;
    const schema = dynamicFieldValidationSchema[fieldKey];
    if (!schema) return true; // No validation defined

    try {
      if (fieldKey === "faxNumber" && isOnlyCountryCode(inputValue)) {
        setError("Fax cannot be empty");
        return false;
      }
      await schema.validate(inputValue);
      return true;
    } catch (validationError: any) {
      setError(validationError.message);
      return false;
    }
  };

  const handleCancel = () => {
    setSelectedField({
      label: "",
      id: 0,
      value: "",
    });
    setInputValue("");
    setMenuItems((prevItems) =>
      prevItems.map((item) =>
        item.value === selectedActions
          ? { ...item, isOpen: !item.isOpen }
          : item
      )
    );
    handleMassActionMenuClose();
    setDisableUpdate(true);
  };

  const formatPayLoad = () => {
    const data = {
      leadsIds: leadsIds,
      fieldToUpdate: { [selectedField.value]: inputValue },
    };
    return data;
  };

  const handleUpdate = async () => {
    const isValid = await validateFieldInput();
    if (!isValid) return;
    // AFP validation check
    const hasInvalidAFPLeads =
      leadsData?.length >= 1 &&
      leadsData?.some(
        (lead: any) =>
          leadsIds.includes(lead?.id) &&
          lead.lead_source === "AFP" &&
          selectedField?.label === "Lead Status" &&
          (inputValue === "New Lead" || inputValue === "Not Qualified")
      );

    if (hasInvalidAFPLeads) {
      setAlertMessage(
        `Some of the leads you selected had source "AFP", so you can't update the lead status to "New Lead" or "Not Qualified".`
      );
      setAlertType("error");
      setAlertOpen(true);
      return;
    }

    // 🚫 Closed-Lost check for Tenant type
    const hasClosedLostLeads =
      tenantDetails?.tenantType === "Tenant" &&
      leadsData?.some(
        (lead: any) =>
          leadsIds?.includes(lead?.id) &&
          lead?.lead_status === "Closed-Lost" &&
          lead?.lead_source === "AFP"
      );

    if (hasClosedLostLeads) {
      setAlertMessage(
        `You can't edit any details of leads that have status "Closed-Lost" and source "AFP".`
      );
      setAlertType("error");
      setAlertOpen(true);
      return;
    }

    setLoading(true);
    setError("");
    let response;
    try {
      response = await massUpdateLeads(formatPayLoad());
      if (response?.status === 200) {
        setAlertMessage(
          `${leadsIds?.length === 1 ? "Record" : "Record(s)"} updated successfully.`
        );
        setAlertType("success");
        setselectedLeadActionScope("All Leads");
        setSelectedLeadsMap({});
        fetchLeads();
      }
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
          "An error occurred while saving the lead."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
      setAlertOpen(true);
      handleCancel();
    }
  };

  const handleChange = (
    _: any,
    newValue: { label: string; id: number; value: string } | null
  ) => {
    setSelectedField(
      newValue ? { ...newValue } : { label: "", id: 0, value: "" }
    );
    setDisableUpdate(!newValue);
    setSelectedInterestedProduct([]);
    setInputValue("");
    setError("");
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target?.value;
    if (e.target.name === "annualRevenue") {
      if (/^\d*\.?\d*$/.test(input) && input.length <= 15) {
        setInputValue(input);
      }
    } else if (e.target.name === "numberOfEmployees") {
      if (input?.length <= 7) {
        setInputValue(input);
      }
    } else if (e.target.name === "leadValue") {
      if (/^\d*\.?\d*$/.test(input) && input.length <= 15) {
        setInputValue(input);
      }
    } else setInputValue(e.target.value);
    setError("");
  };

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  const useStyles = makeStyles(() => ({
    selected: {},
    rootMenuItem: {
      "&$selected": {
        backgroundColor: "transparent !important",
        "&:hover": {
          backgroundColor: "#8879501A !important",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
        },
      },
    },
  }));

  const getOptions = useMemo(() => {
    if (canWrite) {
      return leadEditableFields;
    }
    return leadEditableFields.filter((field) => field.value !== "leadOwner");
  }, [canWrite]);

  const fetchPlaceHolder = () => {
    if (selectedField?.label) {
      return `Enter ${selectedField?.label}`;
    }
  }

  return (
    <div className="mass-update-wrapper">
      <Dialog
        open={isOpen}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className="update-popup-wrapper"
        slotProps={{
          paper: {
            sx: {
              width: isMobile ? "90%" : "570px", // 90% width on mobile
              maxWidth: "95vw",                  // prevent overflow
              minHeight:  "280px",
              borderBottomLeftRadius: '30px',
              borderBottomRightRadius: '30px',
              top: 0,  // add spacing from top on mobile
              margin: 0,
              position: "absolute",
            },
          },
        }}
      >
        <DialogTitle id="alert-dialog-title">{"Update"}</DialogTitle>
        <DialogContent>
          <div className="select-update-wrapper">
            <div className="field-select-wrapper">
              <Autocomplete
                disablePortal={true}
                options={getOptions}
                getOptionLabel={(option) => option.label}
                onChange={handleChange}
                value={
                  [...leadEditableFields].find(
                    (opt) => opt.label === selectedField?.label
                  ) || null
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select a field"
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                  />
                )}
                slotProps={{
                  popper: {
                    sx: { zIndex: 1 },
                  },
                }}
                sx={{
                  width: 250,
                  "& .MuiOutlinedInput-root": {
                    paddingRight: 0,
                    paddingLeft: "9px",
                    padding: 0,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#7D7D7D", // ✅ Default border color
                    },
                  },
                  "& .MuiAutocomplete-input": {
                    padding: "7.5px 5px 7.5px 10px !important",
                  },
                }}
              />
            </div>
            <div className="field-value-wrapper">
              {selectedField?.label &&
                !dropdownLabels.includes(selectedField?.label) && (
                  <>
                    <TextField
                      id="outlined-basic"
                      variant="outlined"
                      required
                      onChange={handleInput}
                      value={inputValue}
                      name={selectedField?.value}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "37px",
                        },
                        paddingLeft: "15px",
                      }}
                      placeholder={fetchPlaceHolder() || "Enter value"}
                    />
                    {error ? <div className="error-message">{error}</div> : ""}
                  </>
                )}
              {selectedField?.label &&
                selectedField?.label === "Interested Product" && (
                  <div className="mass-update-dropdown-wrapper">
                    <SelectInputComponent
                      fieldName="interestedProduct"
                      options={INTERESTED_PRODUCT_OPTIONS}
                      onChange={(e: any) => {
                        setSelectedInterestedProduct(e);
                        setInputValue(e?.length > 0 ? e?.join(", ") : "");
                        setError("");
                      }}
                      value={selectedInterestedProduct}
                      multiple={true}
                      placeholder="Select interested product"
                    />
                    {error ? <div className="error-message">{error}</div> : ""}
                  </div>
                )}
              {selectedField?.label &&
                selectedField?.label === "Confidence Score" && (
                  <div className="mass-update-dropdown-wrapper">
                    <SelectInputComponent
                      fieldName="rating"
                      options={RATING_OPTIONS}
                      onChange={(e: any) => {
                        setError("");
                        setInputValue(e);
                      }}
                      value={inputValue}
                      placeholder="Select confidence score"
                      colorProp={true}
                    />
                    {error ? <div className="error-message">{error}</div> : ""}
                  </div>
                )}
              {/* {
                                selectedField?.label && selectedField?.label === "Lead Source" &&
                                <div className="mass-update-dropdown-wrapper">
                                    <SelectInputComponent
                                        fieldName="leadSource"
                                        options={LEAD_SOURCE_OPTIONS}
                                        onChange={(e: any) => {
                                            setInputValue(e)
                                            setError('')
                                        }}
                                        placeholder="Select source"
                                        value={inputValue}
                                    />
                                    {(error) ? <div className="error-message">{error}</div> : ''}

                                </div>

                            } */}
              {selectedField?.label &&
                selectedField?.label === "Lead Status" && (
                  <div className="mass-update-dropdown-wrapper">
                    <SelectInputComponent
                      fieldName="leadStatus"
                      options={LEAD_STATUS_OPTIONS}
                      onChange={(e: any) => {
                        setInputValue(e);
                        setError("");
                      }}
                      placeholder="Select status"
                      value={inputValue}
                    />
                    {error ? <div className="error-message">{error}</div> : ""}
                  </div>
                )}
              {selectedField?.label &&
                selectedField?.label === "Lead Owner" && (
                  <div className="mass-update-dropdown-wrapper">
                    <SelectInputComponent
                      //added disable to temporary to not to allow edit of lead-owner
                      disabled={true}
                      fieldName="leadOwner"
                      options={LEAD_OWNER_OPTIONS}
                      onChange={(e: any) => {
                        setInputValue(e);
                        setError("");
                      }}
                      value={inputValue}
                      placeholder="Select lead owner"
                    />
                    {error ? <div className="error-message">{error}</div> : ""}
                  </div>
                )}
              {selectedField?.label &&
                selectedField?.label === "Fax Number" && (
                  <div className="fax-number-wrapper mass-update-dropdown-wrapper">
                    <CountryCodeWithPhoneNumber
                      fieldName="fax"
                      placeholder="Enter fax"
                      onChange={(
                        value: string,
                        country: any,
                        one: any,
                        two: any
                      ) => {
                        const countryCode = country?.dialCode;

                        if (!countryCode) {
                          console.warn("Missing dialCode in country object");
                          return;
                        }
                        setError("");
                        setInputValue(two);
                      }}
                      value={inputValue || "+1"}
                      defaultCountry="us"
                      optional={true}
                    />
                    {error ? <div className="error-message">{error}</div> : ""}
                  </div>
                )}
            </div>
          </div>
        </DialogContent>
        <DialogActions className="button-wrapper">
          <Button onClick={handleCancel} className="cancel-btn">
            Cancel
          </Button>
          <Button
            disabled={disableUpdate}
            onClick={handleUpdate}
            autoFocus
            className="update-btn"
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </div>
  );
};

export default MassUpdate;
